import { Refine, Authenticated } from '@refinedev/core';
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';

import { useNotificationProvider, ThemedLayoutV2, ThemedTitleV2 } from '@refinedev/antd';
import '@refinedev/antd/dist/reset.css';

import { App as AntdApp } from 'antd';
import { BrowserRouter, Route, Routes, Outlet } from 'react-router-dom';
import routerBindings, {
  NavigateToResource,
  CatchAllNavigate,
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from '@refinedev/react-router-v6';
import { ColorModeContextProvider } from './contexts/color-mode';
import { Login } from './pages/login';
import { dataProvider } from './utils/data-provider';
import { Header } from './components';
import { Icons } from './components/icons';
import { authProvider } from './utils/auth-provider';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { UsersRoute } from './pages/users/route';
import { ChildrenRoute } from './pages/children/route';
import { ExperiencesRoute } from './pages/experiences/route';
import { OperatorsRoute } from './pages/operators/route';
import { SittersRoute } from './pages/sitters/route';
import { SittingRoute } from './pages/sitting/route';
import { RecommendRuleList } from './pages/engine/recommend/rule/list';
import { RecommendRoute } from './pages/engine/recommend/route';
import { BookRoute } from './pages/book/route';
import { CrownAiScoreExplanations } from './pages/crown-ai/score-explanations';

function App() {
  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <ColorModeContextProvider>
          <AntdApp>
            <DndProvider backend={HTML5Backend}>
              <Refine
                dataProvider={dataProvider}
                notificationProvider={useNotificationProvider}
                routerProvider={routerBindings}
                authProvider={authProvider}
                options={{
                  // syncWithLocation: true,
                  warnWhenUnsavedChanges: true,
                  useNewQueryKeys: true,
                }}
                resources={[
                  {
                    name: 'users',
                    list: '/users',
                    show: '/users/:id',
                    meta: {
                      canCreate: false,
                      canEdit: false,
                      canDelete: false,
                      label: 'ユーザー',
                    },
                  },
                  {
                    name: 'children',
                    list: '/children',
                    show: '/children/:id',
                    meta: {
                      canCreate: false,
                      canEdit: false,
                      canDelete: false,
                      label: '子供',
                    },
                  },
                  {
                    name: 'experienceEvents',
                    list: '/experience/events',
                    show: '/experience/events/:id',
                    create: '/experience/events/create',
                    meta: {
                      parent: 'experience',
                      label: 'イベント',
                    },
                  },
                  {
                    name: 'experience',
                    meta: {
                      label: '体験',
                    },
                  },
                  {
                    name: 'experienceReservationSlots',
                    meta: {
                      label: '予約枠',
                    },
                  },
                  {
                    name: 'experienceReservations',
                    list: '/experience/reservations',
                    show: '/experience/reservations/:id',
                    meta: {
                      parent: 'experience',
                      label: '予約',
                    },
                  },
                  {
                    name: 'experienceSessions',
                    list: '/experience/sessions',
                    show: '/experience/sessions/:id',
                    meta: {
                      parent: 'experience',
                      label: 'レポート',
                    },
                  },
                  {
                    name: 'experienceActions',
                    list: '/experience/actions',
                    meta: {
                      parent: 'experience',
                      label: '発達特徴',
                    },
                  },
                  {
                    name: 'questionnaire',
                    meta: {
                      label: '診断',
                    },
                  },
                  {
                    name: 'questionnaireSurveys',
                    list: '/questionnaire/surveys',
                    show: '/questionnaire/surveys/:id',
                    meta: {
                      parent: 'questionnaire',
                      label: '質問',
                    },
                  },
                  {
                    name: 'questionnaireSessions',
                    list: '/questionnaire/sessions',
                    show: '/questionnaire/sessions/:id',
                    meta: {
                      parent: 'questionnaire',
                      label: '回答',
                    },
                  },
                  {
                    name: 'recommend',
                    meta: {
                      label: 'レコメンド',
                    },
                  },
                  {
                    name: 'engineRecommendRules',
                    list: '/recommend/rules',
                    meta: {
                      parent: 'recommend',
                      label: 'ルール',
                    },
                  },
                  {
                    name: 'engineRecommendItems',
                    list: '/recommend/items',
                    meta: {
                      parent: 'recommend',
                      label: 'データ',
                    },
                  },
                  { name: 'engineDiagnoseInterests' },
                  { name: 'engineDiagnoseAdventureQuotients' },
                  { name: 'engineDiagnoseMultipleIntelligences' },
                  { name: 'engineDiagnoseTemperamentCharacterInventories' },
                  {
                    name: 'books',
                    list: '/books',
                    meta: {
                      label: '絵本',
                    },
                  },
                  {
                    name: 'sitting',
                    meta: {
                      label: 'シッティング',
                    },
                  },
                  {
                    name: 'sittingSchedules',
                    list: '/sitting/schedules',
                    show: '/sitting/schedules/:id',
                    meta: {
                      label: '予定',
                      parent: 'sitting',
                    },
                  },
                  {
                    name: 'sitters',
                    list: '/sitters',
                    meta: {
                      label: 'シッター',
                    },
                  },
                  {
                    name: 'sittingRecords',
                    meta: {
                      label: 'レポート',
                      parent: 'sitting',
                    },
                  },
                  {
                    name: 'operators',
                    list: '/operators',
                    show: '/operators/:id',
                    meta: {
                      label: '管理者',
                    },
                  },
                  {
                    name: 'crownAiScoreExplanations',
                    list: '/crown-ai/score-explanations',
                    meta: {
                      label: 'スコア解説更新',
                    },
                  },
                ]}
              >
                <Routes>
                  {/* 認証後 */}
                  <Route
                    element={
                      <Authenticated
                        key="authenticated-inner"
                        fallback={<CatchAllNavigate to="/login" />}
                        v3LegacyAuthProviderCompatible
                      >
                        <ThemedLayoutV2
                          Header={() => <Header />}
                          Title={({ collapsed }) => (
                            <ThemedTitleV2
                              collapsed={collapsed}
                              text="Crown Admin"
                              icon={<Icons.App />}
                            />
                          )}
                        >
                          <Outlet />
                        </ThemedLayoutV2>
                      </Authenticated>
                    }
                  >
                    <Route index element={<NavigateToResource resource="users" />} />

                    {UsersRoute}
                    {ChildrenRoute}
                    {ExperiencesRoute}
                    {RecommendRoute}
                    {BookRoute}
                    {SittingRoute}
                    {SittersRoute}
                    {OperatorsRoute}

                    <Route
                      path="/crown-ai/score-explanations"
                      element={<CrownAiScoreExplanations />}
                    />
                  </Route>

                  {/* 認証前 */}
                  <Route
                    element={
                      <Authenticated
                        fallback={<Outlet />}
                        key="dashboard"
                        v3LegacyAuthProviderCompatible
                      >
                        <NavigateToResource />
                      </Authenticated>
                    }
                  >
                    <Route path="/login" element={<Login />} />
                  </Route>
                </Routes>
                <RefineKbar />
                <UnsavedChangesNotifier />
                <DocumentTitleHandler />
              </Refine>
            </DndProvider>
          </AntdApp>
        </ColorModeContextProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

export default App;
